/**
 * Building the URL that hands the shopper back to the app that sent them.
 *
 * Every function here takes a `returnUrl` string that arrived over the wire
 * and may be anything. `new URL()` is the whole validation: it throws on a
 * relative path, a bare host, or garbage, and each of these returns `null`
 * rather than propagating a broken string into a `location.replace`. A `null`
 * return is not an error state on the page — it falls back to the generic
 * sara.ai link, which is a worse outcome for the shopper than a redirect and
 * a much better one than a dead navigation.
 *
 * The URL is NOT re-checked against a host allow-list here. It cannot be: the
 * allow-list lives in the Worker, and `GET /api/session/{id}` re-authorises
 * the stored `return_url` against it on every single read, withholding the
 * field entirely when the host is no longer trusted. A browser-side check
 * would be a second, weaker copy of a decision already made by the only party
 * that can make it.
 */

/**
 * The success hand-back: the consumer's return URL carrying the signed
 * receipt and, when known, the order id.
 *
 * `searchParams.set` rather than string concatenation, so a return URL that
 * already carries query parameters of its own (`?next=/read/odyssey/3` is the
 * shape apps/retells registers) keeps them, and so the JWS is percent-encoded
 * exactly once.
 */
export function buildReceiptReturnUrl(
  returnUrl: string,
  receipt: string,
  orderId?: string,
): string | null {
  try {
    const url = new URL(returnUrl);
    url.searchParams.set("receipt", receipt);
    // Truthy, not `!== undefined`, and this is load-bearing: the order id can
    // reach here as an empty string from a bare `?order_id=` in the URL the
    // shopper was sent to. The monorepo tested it this way and so omitted the
    // parameter entirely; an `?order_id=` appended to the consumer's return
    // URL is not the same thing as its absence on the other end.
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    }
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The cancel hand-back. `canceled=1` is the flag the consumer app reads to
 * distinguish "came back from checkout without buying" from "arrived here
 * normally" — no receipt is involved, because nothing was paid.
 */
export function buildCanceledReturnUrl(returnUrl: string): string | null {
  try {
    const url = new URL(returnUrl);
    url.searchParams.set("canceled", "1");
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The host, for naming the destination in a link ("Return to retells.sara.ai").
 *
 * Showing the shopper where they are about to be sent is the point: it is the
 * only moment on this surface where they can tell that the button leads back
 * to the store they started in.
 */
export function hostOf(url: string | undefined): string | null {
  if (url === undefined) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
